import { MONTH_NAMES } from './clinicalUtils';
import {
  COMPANY_BRAND, getLogoBase64, genLabel, captureChartImage,
  BRAND_NAVY_RGB, SEMAFORO_RGB,
  BRAND_NAVY_ARGB, BRAND_NAVY_LIGHT_ARGB, SEMAFORO_ARGB, SEMAFORO_TINT_ARGB,
  drawPdfHeader, drawPdfFooter, drawSectionBarPdf, drawKpiCardsPdf, addChartImagePdf, ensurePdfSpace,
  xlsxSectionHeader, xlsxKpiCards, addChartImageXlsx,
} from './brandAssets';

/**
 * operatorPerformanceExportUtils — exports do relatório "Avaliação por
 * Período" (OperatorPerformanceReportPage.js). Mesmo padrão de
 * `clinicalExportUtils.js`: logo, cabeçalho com identidade visual, tabelas
 * de verdade (jspdf-autotable / exceljs), cores condicionais pelo semáforo
 * já estabelecido em `components/AssessmentTable.js` (getSemaforo) e rodapé
 * paginado. NÃO inventar paleta nova — SEMAFORO_RGB/ARGB de brandAssets.js
 * já reflete exatamente as cores de getSemaforo.
 */

function fullMonthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]}/${y}`;
}

function shortMonthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[m - 1].slice(0, 3)}/${String(y).slice(2)}`;
}

function indicatorSummary(month) {
  if (!month.hasData) return { hit: '—', miss: '—' };
  const hit = month.active.filter((c) => month.assessment[`indicator_${c.slot}`]).map((c) => c.name);
  const miss = month.active.filter((c) => !month.assessment[`indicator_${c.slot}`]).map((c) => c.name);
  return {
    hit: hit.length ? hit.join(', ') : '—',
    miss: miss.length ? miss.join(', ') : '—',
  };
}

function periodLabel(monthlyData) {
  if (!monthlyData.length) return '';
  const first = fullMonthLabel(monthlyData[0].ym);
  const last = fullMonthLabel(monthlyData[monthlyData.length - 1].ym);
  return monthlyData.length === 1 ? first : `${first} a ${last}`;
}

// ─────────────────────────────────────────────────────────────────────────
// PDF (jsPDF + jspdf-autotable)
// ─────────────────────────────────────────────────────────────────────────

export async function exportOperatorReportPdf({ operator, periodDescription, periodSuffix, monthlyData, kpis, chartEl }) {
  const [{ default: jsPDF }, { default: autoTable }, chart] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    captureChartImage(chartEl),
  ]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const logoB64 = await getLogoBase64();

  let cursorY = drawPdfHeader(doc, {
    logoB64,
    subtitle: `Avaliação por Período — ${operator}`,
    meta: `${periodDescription} (${periodLabel(monthlyData)})`,
    pageW,
  });

  const margin = { left: 10, right: 10 };
  const baseHeadStyles = { fillColor: BRAND_NAVY_RGB, textColor: [255, 255, 255], fontStyle: 'bold' };
  const baseStyles = { fontSize: 8.5, cellPadding: 3, textColor: [50, 50, 50] };
  const alternateRowStyles = { fillColor: [248, 249, 251] };
  const contentWidth = pageW - margin.left - margin.right;

  // ── Resumo do período — cards de KPI ──
  cursorY = drawSectionBarPdf(doc, { y: cursorY, width: contentWidth, text: 'RESUMO DO PERÍODO' });
  if (kpis) {
    cursorY = drawKpiCardsPdf(doc, {
      startY: cursorY,
      pageW,
      perRow: 3,
      cards: [
        { label: 'Pontuação média', value: `${kpis.avg.toFixed(1)} pts`, sub: `${kpis.totalMonths - kpis.missing} de ${kpis.totalMonths} meses avaliados` },
        { label: 'Melhor mês', value: `${kpis.best.score} pts`, color: SEMAFORO_RGB[kpis.best.sem.cls], sub: shortMonthLabel(kpis.best.ym) },
        { label: 'Pior mês', value: `${kpis.worst.score} pts`, color: SEMAFORO_RGB[kpis.worst.sem.cls], sub: shortMonthLabel(kpis.worst.ym) },
        { label: 'Verde / amarelo / vermelho', value: `${kpis.counts.verde} / ${kpis.counts.amarelo} / ${kpis.counts.vermelho}` },
        { label: 'Meses sem avaliação', value: `${kpis.missing} de ${kpis.totalMonths}` },
        { label: 'Tendência', value: kpis.trend.label },
      ],
    });
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('Sem avaliações no período selecionado.', margin.left, cursorY + 4);
    cursorY += 12;
  }

  // ── Gráfico: evolução mensal ──
  if (chart) {
    const imgH = contentWidth * (chart.height / chart.width);
    cursorY = ensurePdfSpace(doc, cursorY, 7 + 4 + imgH + 8);
    cursorY = drawSectionBarPdf(doc, { y: cursorY, width: contentWidth, text: 'GRÁFICO — EVOLUÇÃO MENSAL' });
    cursorY = addChartImagePdf(doc, { chart, y: cursorY, maxWidth: contentWidth });
  }

  // ── Evolução mensal ──
  const headers = ['Mês', 'Pontuação', 'Semáforo', 'Departamento', 'Indicadores batidos'];
  autoTable(doc, {
    startY: cursorY,
    margin,
    styles: { ...baseStyles, fontSize: 7.8 },
    head: [
      [{ content: 'EVOLUÇÃO MENSAL', colSpan: headers.length, styles: { ...baseHeadStyles, fontSize: 10 } }],
      headers,
    ],
    headStyles: { fillColor: [46, 64, 84], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.8 },
    body: monthlyData.map((m) => {
      if (!m.hasData) {
        return [shortMonthLabel(m.ym), '—', '—', '—', 'Sem avaliação neste mês'];
      }
      const { hit } = indicatorSummary(m);
      return [
        shortMonthLabel(m.ym),
        `${m.score} pts`,
        m.sem.label,
        m.assessment.department?.name || '—',
        hit,
      ];
    }),
    columnStyles: { 4: { cellWidth: 70 } },
    alternateRowStyles,
    didParseCell(data) {
      if (data.section !== 'body') return;
      const m = monthlyData[data.row.index];
      if (!m.hasData) {
        data.cell.styles.textColor = [176, 184, 196];
        data.cell.styles.fontStyle = 'italic';
        return;
      }
      if (data.column.index === 2) {
        const color = SEMAFORO_RGB[m.sem.cls];
        if (color) { data.cell.styles.textColor = color; data.cell.styles.fontStyle = 'bold'; }
      }
    },
  });

  drawPdfFooter(doc, `${COMPANY_BRAND} · Avaliação por Período · ${operator}`);

  doc.save(`avaliacao-por-periodo-${operator.replace(/\s+/g, '-').toLowerCase()}-${periodSuffix}.pdf`);
}

// ─────────────────────────────────────────────────────────────────────────
// Excel (exceljs)
// ─────────────────────────────────────────────────────────────────────────

export async function exportOperatorReportXlsx({ operator, periodDescription, periodSuffix, monthlyData, kpis, chartEl }) {
  const [{ default: ExcelJS }, chart] = await Promise.all([
    import('exceljs'),
    captureChartImage(chartEl),
  ]);
  const wb = new ExcelJS.Workbook();
  wb.creator = COMPANY_BRAND;
  wb.created = new Date();
  const ws = wb.addWorksheet('Avaliação por Período');

  const widths = [];
  const trackWidth = (col, text) => {
    const len = String(text ?? '').length;
    widths[col - 1] = Math.max(widths[col - 1] || 0, len);
  };

  let r = 1;
  const logoB64 = await getLogoBase64();
  if (logoB64) {
    const imgId = wb.addImage({ base64: logoB64, extension: 'jpeg' });
    ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: 110, height: 62 } });
    r = 6;
  }

  const setCell = (row, col, value, style = {}) => {
    const cell = ws.getCell(row, col);
    cell.value = value;
    Object.assign(cell, style);
    const approxLen = String(value ?? '').length + (style.numFmt ? 4 : 0);
    trackWidth(col, ' '.repeat(approxLen));
    return cell;
  };

  const sectionHeader = (text, span) => {
    r = xlsxSectionHeader(ws, r, text, span);
  };

  const columnHeaderRow = (headers) => {
    headers.forEach((h, i) => {
      setCell(r, i + 1, h, {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_LIGHT_ARGB } },
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
      });
    });
    r += 1;
  };

  const blank = () => { r += 1; };

  // ── Título ──
  setCell(r, 1, `AVALIAÇÃO POR PERÍODO — ${operator}`, { font: { bold: true, size: 13, color: { argb: BRAND_NAVY_ARGB } } });
  r += 1;
  setCell(r, 1, `${periodDescription} (${periodLabel(monthlyData)})`, { font: { size: 9, color: { argb: 'FF888888' } } });
  r += 1;
  setCell(r, 1, genLabel(), { font: { size: 8, italic: true, color: { argb: 'FF9AA3AF' } } });
  blank();

  // ── Resumo do período — cards de KPI ──
  sectionHeader('RESUMO DO PERÍODO', 6);
  if (kpis) {
    r = xlsxKpiCards(ws, r, [
      { label: 'Pontuação média', value: `${kpis.avg.toFixed(1)} pts` },
      { label: 'Melhor mês', value: `${shortMonthLabel(kpis.best.ym)} — ${kpis.best.score} pts`, colorArgb: SEMAFORO_ARGB[kpis.best.sem.cls] },
      { label: 'Pior mês', value: `${shortMonthLabel(kpis.worst.ym)} — ${kpis.worst.score} pts`, colorArgb: SEMAFORO_ARGB[kpis.worst.sem.cls] },
      { label: 'Verde / amarelo / vermelho', value: `${kpis.counts.verde} / ${kpis.counts.amarelo} / ${kpis.counts.vermelho}` },
      { label: 'Meses sem avaliação', value: `${kpis.missing} de ${kpis.totalMonths}` },
      { label: 'Tendência', value: kpis.trend.label },
    ], { totalCols: 6, perRow: 3 });
  } else {
    setCell(r, 1, 'Sem avaliações no período selecionado.');
    r += 1;
  }
  blank();

  // ── Gráfico: evolução mensal ──
  if (chart) {
    sectionHeader('GRÁFICO — EVOLUÇÃO MENSAL', 6);
    r = addChartImageXlsx(wb, ws, { chart, row: r, widthPx: 600 });
    blank();
  }

  // ── Evolução mensal ──
  const headers = ['Mês', 'Pontuação', 'Semáforo', 'Departamento', 'Indicadores batidos', 'Indicadores não batidos'];
  sectionHeader('EVOLUÇÃO MENSAL', headers.length);
  const evolHeaderRow = r;
  columnHeaderRow(headers);
  for (const m of monthlyData) {
    if (!m.hasData) {
      setCell(r, 1, shortMonthLabel(m.ym));
      setCell(r, 2, '—');
      setCell(r, 3, '—');
      setCell(r, 4, '—');
      setCell(r, 5, 'Sem avaliação neste mês', { font: { italic: true, color: { argb: 'FFB0B8C4' } } });
      setCell(r, 6, '—');
      r += 1;
      continue;
    }
    const { hit, miss } = indicatorSummary(m);
    const semArgb = SEMAFORO_ARGB[m.sem.cls];
    setCell(r, 1, shortMonthLabel(m.ym));
    setCell(r, 2, m.score, { numFmt: '0" pts"' });
    setCell(r, 3, m.sem.label, semArgb ? {
      font: { bold: true, color: { argb: semArgb } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: SEMAFORO_TINT_ARGB[m.sem.cls] } },
    } : {});
    setCell(r, 4, m.assessment.department?.name || '—');
    setCell(r, 5, hit);
    setCell(r, 6, miss);
    r += 1;
  }
  const evolLastRow = r - 1;
  if (evolLastRow >= evolHeaderRow) {
    ws.autoFilter = { from: { row: evolHeaderRow, column: 1 }, to: { row: evolLastRow, column: headers.length } };
  }
  ws.views = [{ state: 'frozen', ySplit: evolHeaderRow }];

  widths.forEach((w, i) => {
    ws.getColumn(i + 1).width = Math.min(Math.max(w + 2, 12), 45);
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `avaliacao-por-periodo-${operator.replace(/\s+/g, '-').toLowerCase()}-${periodSuffix}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
